import { useEffect, useState } from "react";
import Layout from "../components/Layout";
import { api, formatApiError } from "../lib/api";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { RefreshCw, ChevronDown, ChevronUp, Clock } from "lucide-react";

const formatTime = (ts) => {
  if (!ts) return "";
  const d = new Date(ts * 1000);
  return d.toLocaleTimeString("hu-HU", { hour: "2-digit", minute: "2-digit" });
};

const formatPct = (p) => (p == null ? "—" : `${(p * 100).toFixed(1)}%`);

const MARKETS = [
  { key: "over_2_5", label: "Over 2.5" },
  { key: "over_3_5", label: "Over 3.5" },
  { key: "btts", label: "BTTS" },
  { key: "btts_both_halves", label: "BTTS Mindkét félidő" },
];

function MatchRow({ match, expanded, onToggle }) {
  const a = match.analysis || {};
  const fp = match.fair_probs || {};

  const bestMarket = (() => {
    let best = null;
    Object.entries(a).forEach(([k, v]) => {
      if (!v) return;
      const ev = v.prob * v.odds;
      if (!best || ev > best.ev) best = { key: k, ev, ...v };
    });
    return best;
  })();

  return (
    <div className="border-b border-white/5 bg-[#0A0A0A] hover:bg-white/[0.02]">
      <div className="grid grid-cols-12 gap-3 items-center px-5 py-3 cursor-pointer" onClick={onToggle} data-testid={`match-row-${match.id}`}>
        <div className="col-span-1 text-xs font-mono text-zinc-500">{formatTime(match.start_timestamp)}</div>
        <div className="col-span-4">
          <div className="text-sm font-medium">
            {match.home_team} <span className="text-zinc-500">vs</span> {match.away_team}
          </div>
          <div className="text-xs text-zinc-500 mt-0.5 truncate">
            {match.category && <span>{match.category} · </span>}
            {match.tournament}
          </div>
        </div>
        <div className="col-span-2 text-xs">
          {fp["1"] != null && (
            <span className="font-mono">
              <span className="text-zinc-500">1:</span> {(fp["1"] * 100).toFixed(0)}%{" "}
              <span className="text-zinc-500">X:</span> {(fp["X"] * 100).toFixed(0)}%{" "}
              <span className="text-zinc-500">2:</span> {(fp["2"] * 100).toFixed(0)}%
            </span>
          )}
        </div>
        <div className="col-span-4">
          {bestMarket && (
            <div className="flex items-center gap-2">
              <Badge variant="outline" className="border-[#007AFF]/30 text-[#3395FF] bg-[#007AFF]/5">
                {bestMarket.key.replace(/_/g, " ").toUpperCase()}
              </Badge>
              <span className="text-xs font-mono text-[#22C55E]">{formatPct(bestMarket.prob)}</span>
              <span className="text-xs font-mono text-zinc-400">@ {bestMarket.odds.toFixed(2)}</span>
            </div>
          )}
        </div>
        <div className="col-span-1 text-right text-zinc-500">
          {expanded ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
        </div>
      </div>

      {expanded && (
        <div className="px-5 pb-4 pt-1 grid grid-cols-2 md:grid-cols-4 gap-px bg-white/5">
          {MARKETS.map((mk) => {
            const v = a[mk.key];
            if (!v) {
              return (
                <div key={mk.key} className="bg-[#0A0A0A] p-3">
                  <div className="text-[10px] tracking-wider text-zinc-600 uppercase">{mk.label}</div>
                  <div className="text-sm text-zinc-600 mt-1">—</div>
                </div>
              );
            }
            const value = v.value > 0.05;
            return (
              <div key={mk.key} className={`bg-[#0A0A0A] p-3 ${value ? "value-glow" : ""}`}>
                <div className="text-[10px] tracking-wider text-zinc-500 uppercase">{mk.label}</div>
                <div className="flex items-center justify-between mt-1.5">
                  <span className="font-mono text-base font-bold text-[#22C55E]">{formatPct(v.prob)}</span>
                  <span className="font-mono text-sm text-white">{v.odds.toFixed(2)}</span>
                </div>
                {value && (
                  <Badge className="mt-1 bg-[#22C55E]/15 text-[#22C55E] border border-[#22C55E]/30 text-[10px]">
                    VALUE +{(v.value * 100).toFixed(1)}%
                  </Badge>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

export default function Dashboard() {
  const [matches, setMatches] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [fetchedAt, setFetchedAt] = useState(null);
  const [windowEnd, setWindowEnd] = useState(null);
  const [hoursAhead, setHoursAhead] = useState(6);
  const [source, setSource] = useState(null);
  const [requestedSource, setRequestedSource] = useState(null);
  const [sourceError, setSourceError] = useState(null);
  const [expandedId, setExpandedId] = useState(null);

  const load = async (refresh = false, hours = hoursAhead) => {
    setLoading(true);
    setError("");
    try {
      const { data } = await api.get("/matches/today", { params: { refresh, hours_ahead: hours } });
      setMatches(data.matches || []);
      setFetchedAt(data.fetched_at);
      setWindowEnd(data.window_end);
      setSource(data.source);
      setRequestedSource(data.requested_source);
      setSourceError(data.error);
    } catch (err) {
      setError(formatApiError(err));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const onHoursChange = (h) => {
    const n = parseInt(h);
    setHoursAhead(n);
    load(false, n);
  };

  const formatDateTime = (s) => (s ? new Date(s).toLocaleString("hu-HU", { hour: "2-digit", minute: "2-digit", day: "2-digit", month: "2-digit" }) : "—");

  return (
    <Layout>
      <div className="px-8 py-6">
        <div className="flex items-end justify-between mb-6 pb-5 border-b border-white/10">
          <div>
            <div className="text-[10px] tracking-[0.3em] text-zinc-500 uppercase mb-1">Mai labdarúgás</div>
            <h1 className="font-display text-3xl font-bold tracking-tight">Mérkőzések</h1>
            <p className="text-sm text-zinc-400 mt-1">
              Élő odds-ok és modellalapú valószínűségek a következő időablakra.
            </p>
          </div>
          <div className="flex items-center gap-3">
            <div className="flex items-center gap-2 px-3 py-2 border border-white/10 bg-[#141414]">
              <Clock size={14} className="text-zinc-500" />
              <span className="text-[10px] tracking-[0.2em] text-zinc-500 uppercase">Időablak</span>
              <Select value={String(hoursAhead)} onValueChange={onHoursChange}>
                <SelectTrigger data-testid="hours-ahead-select" className="bg-transparent border-0 h-7 px-1 text-sm font-mono w-[90px] focus:ring-0">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-[#141414] border-white/10">
                  {[1, 2, 3, 6, 12, 24, 48].map((h) => (
                    <SelectItem key={h} value={String(h)} data-testid={`hours-ahead-${h}`}>
                      Köv. {h} óra
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <Button
              onClick={() => load(true)}
              disabled={loading}
              data-testid="refresh-matches-btn"
              variant="outline"
              className="border-white/15 hover:bg-white/5 text-zinc-200"
            >
              <RefreshCw size={14} className={`mr-2 ${loading ? "animate-spin" : ""}`} />
              Frissítés
            </Button>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-4 gap-px bg-white/10 border border-white/10 mb-6">
          <div className="bg-[#0A0A0A] p-4">
            <div className="text-[10px] tracking-[0.2em] text-zinc-500 uppercase">Most</div>
            <div className="font-mono text-lg font-bold mt-1" data-testid="current-time">
              {new Date().toLocaleTimeString("hu-HU", { hour: "2-digit", minute: "2-digit" })}
            </div>
          </div>
          <div className="bg-[#0A0A0A] p-4">
            <div className="text-[10px] tracking-[0.2em] text-zinc-500 uppercase">Időablak vége</div>
            <div className="font-mono text-lg font-bold mt-1" data-testid="window-end-label">
              {formatDateTime(windowEnd)}
            </div>
          </div>
          <div className="bg-[#0A0A0A] p-4">
            <div className="text-[10px] tracking-[0.2em] text-zinc-500 uppercase">Talált meccs</div>
            <div className="font-mono text-lg font-bold mt-1" data-testid="match-count">{matches.length}</div>
          </div>
          <div className="bg-[#0A0A0A] p-4">
            <div className="text-[10px] tracking-[0.2em] text-zinc-500 uppercase">Adatforrás</div>
            <div className="font-mono text-lg font-bold mt-1" data-testid="data-source-label">
              {source === "demo" ? "Demo" : source === "sofascore" ? "SofaScore" : source === "api_football" ? "API-Football" : "—"}
            </div>
          </div>
        </div>

        {error && (
          <div className="mb-4 px-4 py-3 border border-[#EF4444]/30 bg-[#EF4444]/10 text-sm text-[#EF4444]">
            {error}
          </div>
        )}

        {sourceError && requestedSource && requestedSource !== source && (
          <div className="mb-4 px-4 py-3 border border-yellow-500/30 bg-yellow-500/10 text-sm text-yellow-300" data-testid="source-fallback-warning">
            ⚠️ A kért adatforrás (<span className="font-mono">{requestedSource}</span>) nem érhető el, így demo módban fut.
            Hiba: <span className="font-mono text-xs">{sourceError}</span>
          </div>
        )}

        {loading && matches.length === 0 ? (
          <div className="text-center py-16 text-zinc-500">Adatok lekérése...</div>
        ) : matches.length === 0 ? (
          <div className="text-center py-16 border border-dashed border-white/10 text-zinc-400">
            Ma nem található elemzhető mérkőzés.
          </div>
        ) : (
          <div className="border border-white/10">
            <div className="grid grid-cols-12 gap-3 px-5 py-2 border-b border-white/10 bg-white/[0.02] text-[10px] tracking-wider text-zinc-500 uppercase">
              <div className="col-span-1">Kezdés</div>
              <div className="col-span-4">Mérkőzés</div>
              <div className="col-span-2">1X2</div>
              <div className="col-span-4">Legjobb piac</div>
              <div className="col-span-1 text-right" />
            </div>
            {matches.map((m) => (
              <MatchRow
                key={m.id}
                match={m}
                expanded={expandedId === m.id}
                onToggle={() => setExpandedId(expandedId === m.id ? null : m.id)}
              />
            ))}
          </div>
        )}
      </div>
    </Layout>
  );
}
